import { Component } from 'react';

import DrawIcon from '@mui/icons-material/Draw';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import AddIcon from '@mui/icons-material/Add';
import { Avatar, Box, Button, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import heic2any from 'heic2any';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import { withRouter } from 'providers';
import Slider from 'react-slick';
import { toast } from 'react-toastify';
import { flowRight as compose } from 'lodash';

import AxlDialog from 'components/AxlDialog';
import MuiUploadImage from 'components/MuiUploadImage';
import acl from 'constants/acl';
import { convertImage } from 'Utils/image';
import { IMAGE_TYPES, images } from '../../constants/images';
import styles from './styles';

const configFileSize = import.meta.env.REACT_APP_FILE_SIZE_LIMIT || 15;

class PODUploader extends Component {
  render() {
    const { uid } = this.props;
    if (!uid) return <span></span>;
    const comps = uid.split('_');
    if (comps[0] === 'CS') return <span>Customer</span>;
    if (comps[0] === 'DR') return <span>Driver {comps[1]} </span>;
    if (comps[0] === 'US') return <span>User {comps[1]} </span>;
    return <span>{uid}</span>;
  }
}

class ShipmentPODRequirementCompose extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isShowPanel: true,
      showShipmentPicture: false,
      showSignaturePicture: false,
      showIdCardPicture: false,
      showImageUploadPreView: false,
      imagePOD: null,
      isUploading: false,
      isConverting: false,
    };
  }

  addImage = () => {
    const { store, shipment } = this.props;
    const { shipmentStore } = store;
    const { selectedStop } = shipmentStore;
    this.setState({ isUploading: true });
    shipmentStore.addImage(shipment.id, selectedStop.id, this.state.imagePOD, (data) => {
      this.setState({ isUploading: false });

      if (!data?.id) {
        toast.error(data?.message || 'Failed to upload image');
        return;
      }

      toast.success('Image uploaded successfully');
      this.setState({ showImageUploadPreView: false });
    });
  };

  parsePDFContent = (content) => {
    const lines = content.split('\n');
    const data = { content };
    if (lines?.length > 0) {
      lines.forEach((line) => {
        if (line.startsWith('DBB')) {
          const dobString = line.substring(3);
          const dob = new Date(dobString.substring(4), dobString.substring(0, 2), dobString.substring(2, 4));
          data['dob'] = dob;
        }

        if (line.startsWith('DCS') || line.startsWith('DAB') || line.startsWith('DBO')) {
          data['lastName'] = line.substring(3);
        }

        if (line.startsWith('DAC') || line.startsWith('DBP') || line.startsWith('DCT')) {
          data['firstName'] = line.substring(3);
        }
      });
    }
    return data;
  };

  handleChangeImage = async (evt) => {
    const image = evt.target.files[0];
    if (!image) return;

    const type = image.type;
    if (!IMAGE_TYPES.includes(type)) {
      toast.error('Image type is not supported');
      return;
    }

    this.setState({ isConverting: true, imagePOD: null });
    if (!['image/heic', 'image/heif'].includes(type)) {
      const result = await convertImage(image, 'jpeg');
      this.setState({ imagePOD: result, isConverting: false });
      return;
    }

    let blobURL = URL.createObjectURL(image);
    let blobRes = await fetch(blobURL);
    let blob = await blobRes.blob();
    let conversionResult = await heic2any({
      blob,
      toType: 'image/jpeg',
      quality: 0.8,
    });

    this.setState({ imagePOD: conversionResult, isConverting: false });
  };

  render() {
    const { isShowPanel, showShipmentPicture, showSignaturePicture, showIdCardPicture, showImageUploadPreView, imagePOD, isUploading, isConverting } =
      this.state;
    const { store } = this.props;
    const { shipmentStore, permissionStore } = store;
    const { selectedStop, selectedShipment } = shipmentStore;

    if (!selectedStop || !selectedShipment) return null;

    const info = selectedStop ? selectedStop.info : null;
    const stopImages = info?.images ? info.images : [];
    const stopSignatures = info?.signatures ? info.signatures : [];
    const stopIdcards = info?.idcards ? info.idcards : [];
    const imagesCount = info ? stopImages.length : 0;
    const signaturesCount = info ? stopSignatures.length : 0;
    const idcardsCount = info ? stopIdcards.length : 0;
    const photoRequired = Boolean(selectedShipment.delivery_proof_photo_required);
    const signatureRequired = Boolean(selectedShipment.signature_required);
    const idRequired = Boolean(selectedShipment.id_required);
    const requiredBorderStyle = '1px solid #f5a623';

    const settings = {
      dots: false,
      infinite: false,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
      nextArrow: <SliderNextArrow />,
      prevArrow: <SliderPrevArrow />,
    };

    const isDeniedUploadPOD = permissionStore.isDenied(acl.ASSIGNMENTS.UPLOAD_POD);

    return (
      <Box sx={styles.panelContainer}>
        <div style={styles.panelHeader}>
          <div style={styles.panelHeaderTitle}>Proof of Delivery:</div>
          <div style={styles.panelHeaderRight}>
            <div style={styles.panelHeaderArrow} onClick={() => this.setState({ isShowPanel: !isShowPanel })}>
              <i className={!isShowPanel ? 'fa fa-angle-down' : 'fa fa-angle-up'} />
            </div>
          </div>
        </div>
        {isShowPanel && (
          <div style={styles.buttonGroupWrapper}>
            <div>
              <div style={styles.buttonWrapper}>
                <div style={{ border: photoRequired ? requiredBorderStyle : undefined }}>
                  <Tooltip title="View shipment photo">
                    <IconButton
                      onClick={() => this.setState({ showShipmentPicture: true })}
                      sx={{ width: 50, bgcolor: '#fff', borderRadius: 0, fontSize: 16, p: '5px', border: '1px solid #ccc' }}
                    >
                      <CameraAltIcon fontSize="inherit" />
                      <Box pl={0.5} sx={{ fontSize: 14, fontWeight: 600 }}>
                        {imagesCount}
                      </Box>
                    </IconButton>
                  </Tooltip>
                  {!isDeniedUploadPOD && (
                    <Tooltip title="Add photo">
                      <IconButton
                        onClick={() => this.setState({ showImageUploadPreView: true })}
                        sx={{ width: 32, bgcolor: '#fff', borderRadius: 0, fontSize: 16, p: '5.5px', border: '1px solid #ccc' }}
                      >
                        <AddIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              </div>
              {photoRequired && <div style={styles.requiredText}>(required)</div>}
            </div>
            {Boolean(signatureRequired || signaturesCount) && (
              <div>
                <div style={styles.buttonWrapper}>
                  <div style={{ border: signatureRequired ? requiredBorderStyle : undefined }}>
                    <Tooltip title="View signature">
                      <IconButton
                        onClick={() => this.setState({ showSignaturePicture: true })}
                        sx={{ width: 50, bgcolor: '#fff', borderRadius: 0, fontSize: 16, p: '5px', border: '1px solid #ccc' }}
                      >
                        <DrawIcon fontSize="inherit" />
                        <span style={styles.count}>{signaturesCount}</span>
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
                {signatureRequired && <div style={styles.requiredText}>(required)</div>}
              </div>
            )}
            {Boolean(idRequired || idcardsCount) && (
              <div>
                <div style={styles.buttonWrapper}>
                  <div style={{ border: idRequired ? requiredBorderStyle : undefined }}>
                    <Tooltip title="View ID card info">
                      <IconButton
                        onClick={() => this.setState({ showIdCardPicture: true })}
                        sx={{ width: 50, bgcolor: '#fff', borderRadius: 0, fontSize: 16, p: '5px', border: '1px solid #ccc' }}
                      >
                        <img src={images.icon.idcard} alt="ID Card" style={{ width: '16px', height: 'auto' }} />
                        <Box pl={0.5} sx={{ fontSize: 14, fontWeight: 600 }}>
                          {idcardsCount}
                        </Box>
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
                {idRequired && <div style={styles.requiredText}>(required)</div>}
              </div>
            )}
          </div>
        )}
        <AxlDialog
          isOpen={showShipmentPicture && stopImages.length > 0}
          handleClose={() => this.setState({ showShipmentPicture: false })}
          childrenTitle="Shipment Photos"
        >
          <div style={styles.wrapImages}>
            {stopImages && (
              <Slider {...settings}>
                {stopImages.map((img, index) => (
                  <div key={`image-${index}`}>
                    <div style={{ height: '100%' }}>
                      <img src={img.url} style={styles.imageShipment} alt="" />
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <span style={{ display: 'inline-block', padding: '4px 8px', backgroundColor: '#eee', borderRadius: '4px', marginTop: '4px' }}>
                        Uploaded By: <PODUploader uid={img.uploader} />
                      </span>
                    </div>
                  </div>
                ))}
              </Slider>
            )}
          </div>
        </AxlDialog>
        <AxlDialog
          isOpen={showSignaturePicture && stopSignatures.length > 0}
          handleClose={() => this.setState({ showSignaturePicture: false })}
          childrenTitle="Signatures"
        >
          <div style={styles.wrapImages}>
            {stopSignatures && (
              <Slider {...settings}>
                {stopSignatures.map((img, index) => (
                  <div key={`signature-${index}`}>
                    <div style={{ height: 'calc(100% - 20px)' }}>
                      <img src={img.data} style={styles.imageShipment} alt="signature" />
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <span style={{ display: 'inline-block', padding: '4px 8px', backgroundColor: '#eee', borderRadius: '4px', marginTop: '4px' }}>
                        Uploaded By: <PODUploader uid={img.uploader} />
                      </span>
                    </div>
                  </div>
                ))}
              </Slider>
            )}
          </div>
        </AxlDialog>
        <AxlDialog
          isOpen={showIdCardPicture && stopIdcards.length > 0}
          handleClose={() => this.setState({ showIdCardPicture: false })}
          childrenTitle="ID Card Info"
        >
          <div style={styles.wrapImages}>
            {stopIdcards && (
              <Slider {...settings}>
                {stopIdcards.map((img, index) => {
                  if (!img.data) return null;
                  const info = this.parsePDFContent(img.data);

                  return (
                    <div key={`idcard-${index}`}>
                      <div style={{ height: '100%', backgroundColor: '#fff', padding: 30 }}>
                        <table style={{ margin: 'auto', borderSpacing: 10, fontSize: 18 }}>
                          <tr>
                            <td style={{ textAlign: 'right', fontWeight: 'bold' }}>Name:</td>
                            <td style={{ textAlign: 'left' }}>
                              {info.firstName || '-'} {info.lastName || '-'}
                            </td>
                          </tr>
                          <tr>
                            <td style={{ textAlign: 'right', fontWeight: 'bold' }}>Birthday:</td>
                            <td style={{ textAlign: 'left' }}>{info.dob ? moment(info.dob).format('YYYY-MM-DD') : '-'}</td>
                          </tr>
                        </table>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <span
                          style={{ display: 'inline-block', padding: '4px 8px', backgroundColor: '#eee', borderRadius: '4px', marginTop: '4px' }}
                        >
                          Uploaded By: <PODUploader uid={img.uploader} />
                        </span>
                      </div>
                    </div>
                  );
                })}
              </Slider>
            )}
          </div>
        </AxlDialog>
        <AxlDialog
          isOpen={showImageUploadPreView}
          handleClose={() => this.setState({ showImageUploadPreView: false, imagePOD: null })}
          childrenTitle="Upload Photo"
          childrenAction={
            <Box display={'flex'} justifyContent={imagePOD ? 'space-between' : 'flex-end'} width="100%">
              {imagePOD && (
                <Box display={'flex'} alignItems={'center'}>
                  <input
                    type="file"
                    hidden
                    accept={IMAGE_TYPES.join(', ')}
                    onChange={(evt) => this.handleChangeImage(evt)}
                    id="upload-file"
                    disabled={isUploading}
                  />
                  <label htmlFor="upload-file">
                    <Button variant="outlined" color="inherit" disabled={isUploading || isConverting} component="span" size="small" disableElevation>
                      Change
                    </Button>
                  </label>
                  <Typography style={{ marginLeft: 8 }}>{imagePOD?.name}</Typography>
                </Box>
              )}
              <Box>
                <Button onClick={this.addImage} variant="contained" color="primary" disabled={!imagePOD || isUploading} size="small" disableElevation>
                  {isUploading && <CircularProgress size={24} />}
                  {!isUploading && 'Upload'}
                </Button>
              </Box>
            </Box>
          }
        >
          <Box px={2}>
            {isConverting && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height={200}
                color={'#626262'}
                borderRadius={1}
                border={'1px solid rgba(0, 0, 0, 0.23)'}
              >
                Converting...
              </Box>
            )}

            {imagePOD && (
              <Box display="flex" justifyContent="center" alignItems="center">
                <Avatar variant="square" src={URL.createObjectURL(imagePOD)} style={{ width: '100%', height: '100%' }} />
              </Box>
            )}

            {!imagePOD && !isConverting && (
              <MuiUploadImage
                handleChange={(evt) => this.handleChangeImage(evt)}
                subTitle={`Support file types: ${IMAGE_TYPES.join(', ')}. Upload here…`}
                maxSize={configFileSize}
                fieldName={'image_pod'}
              />
            )}
          </Box>
        </AxlDialog>
      </Box>
    );
  }
}
export const ShipmentPODRequirement = compose(withRouter, inject('store'), observer)(ShipmentPODRequirementCompose);

export default ShipmentPODRequirement;

function SliderPrevArrow(props) {
  const { onClick } = props;
  return (
    <div style={{ ...styles.arrowButton, ...styles.arrowPrev }} onClick={onClick}>
      <i style={styles.arrowIcon} className="fa fa-angle-left" />
    </div>
  );
}

function SliderNextArrow(props) {
  const { onClick } = props;
  return (
    <div style={{ ...styles.arrowButton, ...styles.arrowNext }} onClick={onClick}>
      <i style={styles.arrowIcon} className="fa fa-angle-right" />
    </div>
  );
}
